import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import {
  Braces,
  CheckCircle2,
  Clock3,
  Columns,
  Database,
  Eye,
  FileSearch,
  Lock,
  Play,
  Rows,
  ShieldCheck,
  Table2,
} from 'lucide-react';
import './DataExplorer.css';
import PageHeader from '../components/PageHeader';

const DEFAULT_SQL = 'SELECT status, COUNT(*) c FROM rides GROUP BY status';
const RESULT_CAP = 200;

function Metric({ icon: Icon, label, value, detail, tone = 'neutral' }) {
  return (
    <article className={`data-explorer-metric is-${tone}`}>
      <div className="data-explorer-metric-icon"><Icon size={19} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

export default function DataExplorer() {
  const [tables, setTables] = useState([]);
  const [sql, setSql] = useState(DEFAULT_SQL);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/admin/data/catalog')
      .then(r => setTables(r.data.tables || []))
      .catch(() => {});
  }, []);

  const totalColumns = useMemo(
    () => tables.reduce((sum, table) => sum + (table.columns || []).length, 0),
    [tables],
  );
  const columns = res?.columns || [];
  const rows = res?.rows || [];

  const run = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await api.post('/admin/data/query', { sql });
      setRes(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Fehler');
      setRes(null);
    }
    setBusy(false);
  };

  return (
    <div className="data-explorer-page">
      <PageHeader
        title="Daten-Explorer"
        description="Kontrollierte Ad-hoc-Analysen auf dem VOOGO Datenbestand – für autorisierte Admins und strikt ohne Schreibzugriff."
        actions={
        <div className="data-explorer-hero-badges" aria-label="Datenvertrag">
          <span><Lock size={14} /> Nur lesen</span>
          <span><FileSearch size={14} /> Protokolliert</span>
        </div>
      }
      />

      <section className="data-explorer-boundary" aria-label="Sicherheitsgrenze">
        <div className="data-explorer-boundary-icon"><Database size={23} /></div>
        <div>
          <span>Governed query boundary</span>
          <h2>Read-only Verbindung aktiv</h2>
          <p>Das Backend akzeptiert ausschließlich eine einzelne <code>SELECT</code>- oder <code>WITH</code>-Anweisung.</p>
        </div>
        <div className="data-explorer-boundary-status">
          <span><i /> Zugriff kontrolliert</span>
          <small>Berechtigung · data.explore</small>
        </div>
      </section>

      <section className="data-explorer-metrics" aria-label="Workspace-Kennzahlen">
        <Metric icon={Table2} label="Katalog" value={tables.length} detail="verfügbare Tabellen" tone="purple" />
        <Metric icon={Columns} label="Felder" value={totalColumns} detail="katalogisierte Spalten" />
        <Metric icon={Rows} label="Ergebnislimit" value={RESULT_CAP} detail="Zeilen pro Abfrage" />
        <Metric icon={CheckCircle2} label="Audit" value="Aktiv" detail="jede Abfrage protokolliert" tone="green" />
      </section>

      <section className="data-explorer-workspace">
        <aside className="data-explorer-catalog">
          <div className="data-explorer-section-label">
            <span>01 · Schema</span>
            <strong>{tables.length}</strong>
          </div>
          <div className="data-explorer-catalog-heading">
            <h2>Tabellenkatalog</h2>
            <p>Tabelle wählen, um eine sichere Startabfrage in den Editor zu übernehmen.</p>
          </div>
          <div className="data-explorer-table-list">
            {tables.map(table => (
              <button
                type="button"
                className="data-explorer-table-button"
                key={table.name}
                onClick={() => setSql(`SELECT * FROM ${table.name} LIMIT 50`)}
                title={`SELECT * FROM ${table.name}`}
              >
                <span className="data-explorer-table-icon"><Table2 size={15} /></span>
                <span className="data-explorer-table-copy">
                  <strong>{table.name}</strong>
                  <small>{(table.columns || []).join(', ')}</small>
                </span>
                <span className="data-explorer-column-count">{(table.columns || []).length}</span>
              </button>
            ))}
            {tables.length === 0 && (
              <div className="data-explorer-catalog-empty">
                <Database size={20} />
                <span>Katalog wird geladen</span>
              </div>
            )}
          </div>
        </aside>

        <div className="data-explorer-main">
          <section className="data-explorer-console">
            <div className="data-explorer-console-header">
              <div>
                <span>02 · Query</span>
                <h2>SQL Workspace</h2>
              </div>
              <div className="data-explorer-console-status"><Eye size={14} /> Read-only</div>
            </div>

            <div className="data-explorer-editor">
              <div className="data-explorer-editor-bar">
                <span><Braces size={14} /> query.sql</span>
                <small>SELECT / WITH · Einzelanweisung</small>
              </div>
              <textarea
                aria-label="SQL-Abfrage"
                value={sql}
                onChange={e => setSql(e.target.value)}
                onKeyDown={e => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') run();
                }}
                spellCheck={false}
              />
            </div>

            <div className="data-explorer-console-actions">
              <button type="button" className="data-explorer-run" onClick={run} disabled={busy}>
                <Play size={15} fill="currentColor" />
                {busy ? 'Abfrage läuft…' : 'Abfrage ausführen'}
              </button>
              <div className="data-explorer-shortcut">
                <span>⌘ / Strg</span>
                <span>↵</span>
              </div>
              <small>maximal {RESULT_CAP} Zeilen</small>
            </div>
          </section>

          {err && (
            <div className="data-explorer-error" role="alert">
              <span>SQL-Abfrage nicht ausgeführt</span>
              <strong>{err}</strong>
            </div>
          )}

          <section className="data-explorer-results">
            <div className="data-explorer-results-header">
              <div>
                <span>03 · Output</span>
                <h2>Abfrageergebnis</h2>
              </div>
              {res && (
                <div className="data-explorer-result-meta" aria-label="Ergebnisstatus">
                  <span><Rows size={14} /> {res.count} Zeile{res.count === 1 ? '' : 'n'}</span>
                  <span><Columns size={14} /> {columns.length} Spalten</span>
                  <span><Clock3 size={14} /> {res.ms} ms</span>
                </div>
              )}
            </div>

            {!res ? (
              <div className="data-explorer-ready">
                <div><Database size={25} /></div>
                <strong>Bereit für eine kontrollierte Abfrage</strong>
                <p>Das Ergebnis erscheint hier, ohne die Workspace-Navigation zu verlassen.</p>
              </div>
            ) : (
              <>
                <div className="data-explorer-result-scroll">
                  <table>
                    <thead>
                      <tr>{columns.map(column => <th key={column}>{column}</th>)}</tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={index}>
                          {columns.map(column => (
                            <td key={column}>
                              {row[column] === null
                                ? <span className="data-explorer-null">null</span>
                                : String(row[column])}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td className="data-explorer-no-rows" colSpan={Math.max(1, columns.length)}>Keine Zeilen</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <footer className="data-explorer-result-footer">
                  <span><i /> Abfrage abgeschlossen</span>
                  <small>
                    {res.count} Zeile{res.count === 1 ? '' : 'n'} · {res.ms} ms
                    {res.truncated ? ` · auf ${RESULT_CAP} begrenzt` : ''}
                  </small>
                </footer>
              </>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}
